import { Fragment, type ReactNode } from 'react';
import { Dialog, Transition } from '@headlessui/react';
import { X } from 'lucide-react';
import { cn } from '../utils/cn';

interface DrilldownModalProps {
  isOpen: boolean;
  onClose: () => void;
  title: string;
  subtitle?: string;
  children: ReactNode;
  actions?: ReactNode;
  size?: 'md' | 'lg' | 'xl' | 'full';
}

const sizeMap = {
  md: 'max-w-2xl',
  lg: 'max-w-4xl',
  xl: 'max-w-6xl',
  full: 'max-w-[95vw]',
};

export default function DrilldownModal({ isOpen, onClose, title, subtitle, children, actions, size = 'lg' }: DrilldownModalProps) {
  return (
    <Transition appear show={isOpen} as={Fragment}>
      <Dialog as="div" className="relative z-50" onClose={onClose}>
        <Transition.Child
          as={Fragment}
          enter="ease-out duration-200"
          enterFrom="opacity-0"
          enterTo="opacity-100"
          leave="ease-in duration-150"
          leaveFrom="opacity-100"
          leaveTo="opacity-0"
        >
          <div className="fixed inset-0 bg-black/50" />
        </Transition.Child>

        <div className="fixed inset-0 overflow-y-auto">
          <div className="flex min-h-full items-center justify-center p-4">
            <Transition.Child
              as={Fragment}
              enter="ease-out duration-200"
              enterFrom="opacity-0 scale-95"
              enterTo="opacity-100 scale-100"
              leave="ease-in duration-150"
              leaveFrom="opacity-100 scale-100"
              leaveTo="opacity-0 scale-95"
            >
              <Dialog.Panel className={cn('w-full rounded-xl border border-border bg-surface-card text-content-primary shadow-xl', sizeMap[size])}>
                {/* Header */}
                <div className="flex items-center justify-between border-b border-border px-6 py-4">
                  <div>
                    <Dialog.Title className="text-lg font-semibold text-content-primary">{title}</Dialog.Title>
                    {subtitle && <p className="text-sm text-content-muted mt-0.5">{subtitle}</p>}
                  </div>
                  <div className="flex items-center gap-2">
                    {actions}
                    <button onClick={onClose} className="flex h-8 w-8 items-center justify-center rounded-lg text-content-muted hover:bg-surface-card-hover transition-colors">
                      <X size={18} />
                    </button>
                  </div>
                </div>
                {/* Body */}
                <div className="max-h-[70vh] overflow-y-auto p-6">
                  {children}
                </div>
              </Dialog.Panel>
            </Transition.Child>
          </div>
        </div>
      </Dialog>
    </Transition>
  );
}
